/** Primitive UI reutilizabile, aliniate la tokens (card, buton, empty state, modal, skeleton). */
import { useEffect } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
        marginBottom: 20,
      }}
    >
      <div>
        <h1 className="bricolage" style={{ fontWeight: 800, fontSize: 27 }}>
          {title}
        </h1>
        {subtitle && <p style={{ margin: '7px 0 0', color: 'var(--muted)', fontSize: 14 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

export function Card({ children, style, className, ...rest }: { children: ReactNode; style?: CSSProperties; className?: string } & Record<string, unknown>) {
  return (
    <div className={`card${className ? ' ' + className : ''}`} style={{ padding: 18, ...style }} {...rest}>
      {children}
    </div>
  );
}

export function EmptyState({
  illustration = '🎬',
  title,
  body,
  cta,
}: {
  illustration?: string;
  title: string;
  body?: string;
  cta?: ReactNode;
}) {
  return (
    <div
      className="card rv-in"
      style={{
        padding: '48px 28px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 66,
          height: 66,
          borderRadius: 20,
          display: 'grid',
          placeItems: 'center',
          fontSize: 30,
          background: 'var(--accent-soft)',
        }}
      >
        {illustration}
      </div>
      <h2 className="bricolage" style={{ fontSize: 20, fontWeight: 800 }}>
        {title}
      </h2>
      {body && <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14, maxWidth: 420 }}>{body}</p>}
      {cta && <div style={{ marginTop: 6 }}>{cta}</div>}
    </div>
  );
}

export function Skeleton({ height = 16, width = '100%', style }: { height?: number | string; width?: number | string; style?: CSSProperties }) {
  return <div className="skeleton" style={{ height, width, ...style }} />;
}

export function SkeletonCard() {
  return (
    <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Skeleton height={20} width="60%" />
      <Skeleton height={14} width="90%" />
      <Skeleton height={14} width="75%" />
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  width,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="rv-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="rv-modal" style={width ? { width: `min(${width}px, 100%)` } : undefined} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
            <h2 className="bricolage" style={{ fontSize: 20, fontWeight: 800 }}>
              {title}
            </h2>
            <button onClick={onClose} aria-label="Închide" style={{ color: 'var(--muted)', fontWeight: 800, fontSize: 18 }}>
              ✕
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export function Pill({ children, tone = 'accent', style }: { children: ReactNode; tone?: 'accent' | 'ok' | 'warn' | 'err' | 'muted'; style?: CSSProperties }) {
  const map: Record<string, { bg: string; fg: string }> = {
    accent: { bg: 'var(--accent-soft)', fg: 'var(--accent-text)' },
    ok: { bg: 'var(--ok-soft)', fg: 'var(--ok)' },
    warn: { bg: 'var(--warn-soft)', fg: 'var(--warn)' },
    err: { bg: 'var(--err-soft)', fg: 'var(--err)' },
    muted: { bg: 'var(--bg2)', fg: 'var(--muted)' },
  };
  const c = map[tone] ?? map.accent!;
  return (
    <span className="pill" style={{ background: c.bg, color: c.fg, ...style }}>
      {children}
    </span>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'inline-flex', gap: 4, background: 'var(--bg2)', padding: 4, borderRadius: 12, border: '1px solid var(--line)' }}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          data-active={value === o.value}
          style={{
            padding: '8px 14px',
            borderRadius: 9,
            fontSize: 13,
            fontWeight: 800,
            background: value === o.value ? 'var(--surface)' : 'transparent',
            color: value === o.value ? 'var(--ink)' : 'var(--muted)',
            boxShadow: value === o.value ? 'var(--shadow)' : 'none',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ size = 18 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        border: '2.5px solid var(--line)',
        borderTopColor: 'var(--accent)',
        animation: 'rv-spin 0.7s linear infinite',
      }}
    />
  );
}

/** Bloc de eroare cu retry (spec §3: mesaj + pasul următor). */
export function ErrorBlock({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      className="card"
      style={{ padding: 20, borderColor: 'var(--err-soft)', display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="pill" style={{ background: 'var(--err-soft)', color: 'var(--err)' }}>
          A apărut o problemă
        </span>
      </div>
      <p style={{ margin: 0, fontSize: 14 }}>{message}</p>
      {onRetry && (
        <button className="btn btn-ghost btn-sm" onClick={onRetry} style={{ alignSelf: 'flex-start' }}>
          Încearcă din nou
        </button>
      )}
    </div>
  );
}
